import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';

import { ReportTableComponent } from '../../reports/shared/report-table/report-table.component';
import {
    TabularReportColumn,
    TabularReportRow,
} from '../../../app-layout/shared/tabular-report/tabular-report.config';
import { LinkButtonDirective } from '../../shared/directives/button.directive';
import { GenerateReportCardPayload } from '../../../../services/gable/gable.service';

/**
 * Inline rendering of a `generate_report` tool result inside the Gable
 * chat. Mirrors the layout of the standalone Reports pages
 * (e.g. `/app/reports/profit-vs-loss-ledger`) — same `<app-report-table>`,
 * same Export CSV / Save as PDF buttons — plus a "View in Reports"
 * deeplink that takes the user to the full standalone page if they want
 * filters, comparison mode, etc.
 *
 * Auto-hides each of the three buttons if its url/deeplink is missing:
 *  - PDF/CSV URLs are 24h-signed and always come back on a success
 *  - reports_page_url is null for packets-only reports (see
 *    `Report.route` in ReportsTableSeeder — a handful of entries have
 *    `route: null` because they're only reachable as PDF bundles).
 */
@Component({
    selector: 'app-gable-report-card',
    templateUrl: './report-card.component.html',
    styleUrls: ['./report-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        RouterModule,
        MatButtonModule,
        ReportTableComponent,
        LinkButtonDirective,
    ],
})
export class GableReportCardComponent {
    @Input({ required: true }) payload!: GenerateReportCardPayload;

    get columns(): TabularReportColumn[] {
        return (this.payload?.data?.columns ?? []) as TabularReportColumn[];
    }

    private rowsFor?: GenerateReportCardPayload;
    private rowsCache: TabularReportRow[] = [];

    get rows(): TabularReportRow[] {
        if (this.rowsFor !== this.payload) {
            const rows = (this.payload?.data?.rows ?? []) as TabularReportRow[];
            const keys = this.columns.map(c => c.valueKey).filter(k => !!k && k.includes('_'));
            this.rowsCache = keys.length ? rows.map(r => this.withSnakeKeys(r, keys)) : rows;
            this.rowsFor = this.payload;
        }
        return this.rowsCache;
    }

    /** The box lane camelCases row keys but not the columns' valueKey strings, so alias each snake key back. */
    private withSnakeKeys(row: TabularReportRow, keys: string[]): TabularReportRow {
        const out: TabularReportRow = { ...row };
        for (const key of keys) {
            if (out[key] === undefined) {
                out[key] = row[key.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase())];
            }
        }
        if (Array.isArray(row.children)) {
            out.children = row.children.map(child => this.withSnakeKeys(child, keys));
        }
        return out;
    }

    get pdfUrl(): string | null {
        return this.expired ? null : (this.payload?.downloads?.pdf?.url ?? null);
    }

    /** Box-lane cards arrive camelCased by core's response middleware; drawer-lane cards keep snake_case. */
    private field<T>(source: object | null | undefined, snake: string, camel: string): T | null {
        const record = (source ?? {}) as Record<string, unknown>;
        return (record[snake] ?? record[camel] ?? null) as T | null;
    }

    get csvUrl(): string | null {
        return this.expired ? null : (this.payload?.downloads?.csv?.url ?? null);
    }

    /** Links are signed for 24h; a card re-read from an old conversation says so instead of rendering a dead button. */
    get expired(): boolean {
        const at = this.field<string>(this.payload?.downloads?.pdf, 'expires_at', 'expiresAt')
            ?? this.field<string>(this.payload?.downloads?.csv, 'expires_at', 'expiresAt');
        return !!at && new Date(at).getTime() < Date.now();
    }

    /** The Reports pages cannot take Gable's filters yet, so a link would open them unfiltered; hidden until they can. */
    get reportsPageUrl(): string | null {
        return null;
    }

    get dateRange(): string {
        return this.field<string>(this.payload, 'date_range', 'dateRange') ?? '';
    }

    get reportName(): string {
        return this.field<string>(this.payload, 'report_name', 'reportName') ?? '';
    }

    get orgName(): string {
        return this.field<string>(this.payload, 'org_name', 'orgName') ?? '';
    }
}
